// Helpers shared by the public-page islands (no third-party code is loaded until it is needed).

export function readBoot<T>(): T {
  const el = document.getElementById('boot');
  return (el ? JSON.parse(el.textContent || 'null') : null) as T;
}

export async function postJson<T = unknown>(url: string, body: unknown): Promise<{ ok: boolean; status: number; data: T }> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let data: unknown = null;
  if (res.status !== 204) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  }
  return { ok: res.ok, status: res.status, data: data as T };
}

/** Parse an SSE response body (fetch streaming) and call onEvent for every data frame. */
export async function readSse(res: Response, onEvent: (data: any) => void): Promise<void> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx = buffer.indexOf('\n\n');
    while (idx >= 0) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const data = frame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n');
      if (data) {
        try {
          onEvent(JSON.parse(data));
        } catch {
          /* ignore malformed frame */
        }
      }
      idx = buffer.indexOf('\n\n');
    }
  }
}

declare global {
  interface Window {
    turnstile?: {
      render(el: HTMLElement, opts: Record<string, unknown>): string;
      remove(id: string): void;
    };
  }
}

let turnstileLoading: Promise<void> | null = null;

/** Load Cloudflare Turnstile on demand (only when a scan is requested). */
export function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  turnstileLoading ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load the verification widget.'));
    document.head.appendChild(s);
  });
  return turnstileLoading;
}

/** Render a Turnstile widget into `slot` and resolve with its token. */
export async function turnstileToken(slot: HTMLElement, siteKey: string): Promise<string> {
  await loadTurnstile();
  return new Promise<string>((resolve, reject) => {
    slot.replaceChildren();
    const holder = document.createElement('div');
    slot.appendChild(holder);
    const id = window.turnstile!.render(holder, {
      sitekey: siteKey,
      callback: (token: string) => {
        resolve(token);
        setTimeout(() => window.turnstile?.remove(id), 500);
      },
      'error-callback': () => reject(new Error('Verification failed. Please try again.')),
    });
  });
}

export function progressLabel(e: { type: string; [k: string]: any }): string | null {
  switch (e.type) {
    case 'queued':
      return 'Waiting for a free scanner…';
    case 'loading':
      return 'Loading the page in a fresh browser…';
    case 'checking':
      return `Checking ${e.category}…`;
    case 'finding':
      return `Flagged: ${e.summary}`;
    case 'done':
      return `${e.partial ? 'Incomplete scan' : 'Done'}: ${e.counts?.total ?? 0} flagged elements.${e.partial ? ' Only completed checks are included.' : ''}`;
    case 'error':
      return e.message;
    default:
      return null;
  }
}
